import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { HeaderOne, HeaderThree, TextLink } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import ClassCard from 'components/dashboard/ClassCard'
import { k8V2ContentfulMappings } from 'utils/productMaps'
import { isFlagOn } from 'utils/featureFlags'
import TierLabel from 'components/TierLabel'
import {
  ButtonsContainer,
  StyledAwardIcon,
  StyledButton,
  StyledCreateClassIcon,
  Wrapper,
} from './component.styles'
import { PreviewCourseList } from './PreviewCourseList'
import {
  getDigitalWellBeingPreviewPath,
  getTier2DisplayTitle,
  getTier2PreviewPath,
  isDigitalWellBeingCapabilityEnabledForProgram,
} from './helpers'
import {
  EXPLORE_PROGRAM_DESCRIPTION,
  EXPLORE_PROGRAM_TITLE,
  MY_CLASSES_TITLE,
  TEACH_DESCRIPTION,
  NO_CLASSES_MESSAGE,
  NO_CLASSES_TRAINING_LINK,
} from './constants'

// #region  Section Header
const SectionHeader = ({ title, tierLabel, description }) => (
  <header>
    <div className="header-text">
      <div className="headline">
        <HeaderThree>{title}</HeaderThree>
        {tierLabel && <TierLabel labelText={tierLabel} />}
        <div className="spacer" />
      </div>
      <p>{description}</p>
    </div>
  </header>
)

SectionHeader.propTypes = {
  description: PropTypes.string.isRequired,
  tierLabel: PropTypes.string,
  title: PropTypes.string.isRequired,
}
// #endregion

const getTier1CourseLink = (courseLinks, classItem) =>
  courseLinks?.find(c => classItem?.contentfulID === c?.entryId)

const getTier2ContentfulMapping = classItem =>
  k8V2ContentfulMappings?.find(
    course => classItem?.contentfulID === course?.id,
  ) || {}

const getClassCardDisplayData = ({ classItem, courseLinks, tier }) => {
  const courseTier1 =
    tier === 1 ? getTier1CourseLink(courseLinks, classItem) : null
  const { displayTitle: tier2DisplayTitle, defaultUnit } =
    tier === 2 ? getTier2ContentfulMapping(classItem) : {}

  return {
    courseTier1,
    defaultUnit,
    gradeDisplayTitle: tier === 2 ? tier2DisplayTitle : courseTier1?.title,
  }
}

const createClassCardClickHandler = ({
  tier,
  courseTier1,
  classItem,
  onComponentClick,
  onClassClick,
}) => path => {
  if (onComponentClick) {
    return onComponentClick(path)
  }
  if (onClassClick) {
    // Maintain legacy behavior for tests and older callers
    return tier === 1
      ? onClassClick(courseTier1?.slug, classItem?.instance)()
      : onClassClick() // tier 2 fallback – tests only assert it was called
  }
  return undefined
}

const getPreviewClickHandler = ({
  onComponentClick,
  onCourseClick,
  program,
  getPath,
}) => course =>
  onComponentClick
    ? () => onComponentClick(getPath({ program, course }))
    : onCourseClick(course.slug)

// #region  Class List
const ClassList = ({
  classes,
  courseLinks,
  lessonCompletionGoalValue,
  onComponentClick,
  onClassClick,
  program,
  tier = 1,
}) => (
  <ul className="class-links">
    {classes.map(classItem => {
      const {
        courseTier1,
        defaultUnit,
        gradeDisplayTitle,
      } = getClassCardDisplayData({ classItem, courseLinks, tier })

      return (
        <li key={`class-card-li-${classItem?.instance}`}>
          <ClassCard
            className={classItem?.title}
            dataTestId={`class-card-${classItem?.instance}`}
            defaultUnit={tier === 2 ? defaultUnit : undefined}
            displayTitle={classItem?.title}
            goalPercent={tier === 1 ? lessonCompletionGoalValue : undefined}
            gradeDisplayTitle={gradeDisplayTitle}
            includeProgressBar={tier === 1}
            instance={classItem?.instance}
            onClick={createClassCardClickHandler({
              tier,
              courseTier1,
              classItem,
              onComponentClick,
              onClassClick,
            })}
            program={program}
            scoID={classItem?.scoID}
            tier={tier}
            totalAmount={classItem?.totalLessons}
            totalComplete={classItem?.completeLessons}
          />
        </li>
      )
    })}
  </ul>
)

ClassList.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoID: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      entryId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  program: PropTypes.string,
  tier: PropTypes.number,
}
// #endregion

export const ProductLessons = React.forwardRef(
  (
    {
      classes,
      contentfulCourses,
      courseLinks,
      currentSite,
      lessonCompletionGoalValue,
      onClassClick,
      onComponentClick,
      onCourseClick,
      onCreateClass,
      onCreateSmallGroup,
      program,
      showSmallGroupCreation = true,
      title,
    },
    ref,
  ) => {
    const userHasClasses = !!classes && classes.length > 0

    const tier1Classes = classes?.filter(c => c?.tier !== 2) || []
    const tier2Classes = classes?.filter(c => c?.tier === 2) || []
    const hasTier2Classes = tier2Classes.length > 0

    // TODO: replace the shouldShowOnLessonsPage filter with the real DWB
    // course filter once the DWB content is wired up in contentful.
    const lessonPageCourseLinks =
      courseLinks?.filter(c => c.shouldShowOnLessonsPage) || []
    const tier2CourseLinks =
      courseLinks?.filter(c => !c.shouldShowOnLessonsPage) || []
    const isSpecializedLessonCompletionUpdatesOn = isFlagOn([
      'feature_LEARN-27700_specialized-lesson-completion-updates',
    ])

    const showDigitalWellBeingUnit =
      isSpecializedLessonCompletionUpdatesOn &&
      isDigitalWellBeingCapabilityEnabledForProgram({
        site: currentSite,
        program,
      })

    const handleTier1CoursePreviewClick = course => onCourseClick(course.slug)

    const handleTier2CoursePreviewClick = getPreviewClickHandler({
      onComponentClick,
      onCourseClick,
      program,
      getPath: getTier2PreviewPath,
    })

    const handleDigitalWellBeingCoursePreviewClick = getPreviewClickHandler({
      onComponentClick,
      onCourseClick,
      program,
      getPath: getDigitalWellBeingPreviewPath,
    })

    const getDigitalWellBeingCourseTitle = course =>
      course.title === 'Kindergarten' ? 'Grade K' : course.title

    const navigateTo = path =>
      onComponentClick?.(path, NavigationHelper.types.PUSH)

    const handleCreateClass = useCallback(() => {
      if (onCreateClass) onCreateClass()
    }, [onCreateClass])

    const handleCreateSmallGroup = useCallback(() => {
      if (onCreateSmallGroup) {
        onCreateSmallGroup()
        return
      }
      if (onCreateClass) onCreateClass()
    }, [onCreateSmallGroup, onCreateClass])

    return (
      <Wrapper>
        <header>
          <HeaderOne>{title}</HeaderOne>
        </header>

        <section>
          <SectionHeader
            description={TEACH_DESCRIPTION}
            tierLabel="TIER 1"
            title={MY_CLASSES_TITLE}
          />
          <div>
            {!userHasClasses && (
              <div className="no-classes">
                <p className="no-class-description">{NO_CLASSES_MESSAGE}</p>
                <p>
                  {NO_CLASSES_TRAINING_LINK}{' '}
                  <TextLink href="/dashboard">My Programs</TextLink>.
                </p>
              </div>
            )}
            {userHasClasses && (
              <ClassList
                classes={tier1Classes}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={1}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateClass}>
              <StyledCreateClassIcon />
              {'Create a Class'}
            </StyledButton>
            <StyledButton
              onClick={() => navigateTo(`/product/${program}/trainings`)}
            >
              <StyledAwardIcon />
              {'Training Articles'}
            </StyledButton>
          </ButtonsContainer>
        </section>

        {showSmallGroupCreation && (
          <section id="small-groups">
            <SectionHeader
              description="Teach Tier 2 lessons to small groups and track progress"
              tierLabel="TIER 2"
              title="Small Groups"
            />
            <div>
              {hasTier2Classes && (
                <ClassList
                  classes={tier2Classes}
                  courseLinks={courseLinks}
                  lessonCompletionGoalValue={lessonCompletionGoalValue}
                  onClassClick={onClassClick}
                  onComponentClick={onComponentClick}
                  program={program}
                  tier={2}
                />
              )}
            </div>
            <ButtonsContainer>
              <StyledButton onClick={handleCreateSmallGroup}>
                <StyledCreateClassIcon aria-hidden />
                {'Create a Small Group'}
              </StyledButton>
              <StyledButton
                onClick={() =>
                  navigateTo(`/product/${program}/intervention-resources`)
                }
              >
                <StyledAwardIcon />
                {'Intervention Resources'}
              </StyledButton>
            </ButtonsContainer>
          </section>
        )}

        <section id="explore-the-program">
          <header>
            <div>
              <HeaderThree>{EXPLORE_PROGRAM_TITLE}</HeaderThree>
              <p>{EXPLORE_PROGRAM_DESCRIPTION}</p>
            </div>
          </header>

          {!showDigitalWellBeingUnit && (
            <>
              <HeaderThree>Core Units</HeaderThree>
              <PreviewCourseList
                courseLinks={lessonPageCourseLinks}
                getOnClick={handleTier1CoursePreviewClick}
                getTitle={course => course.title}
                tier={1}
              />
            </>
          )}

          {showDigitalWellBeingUnit && (
            <PreviewCourseList
              courseLinks={lessonPageCourseLinks}
              getOnClick={handleDigitalWellBeingCoursePreviewClick}
              getTitle={getDigitalWellBeingCourseTitle}
              isDigitalWellBeing
              tier={1}
            />
          )}

          {hasTier2Classes && !showDigitalWellBeingUnit && (
            <>
              <HeaderThree>Tier 2 Lessons</HeaderThree>
              <PreviewCourseList
                courseLinks={tier2CourseLinks}
                getOnClick={handleTier2CoursePreviewClick}
                getTitle={getTier2DisplayTitle}
                tier={2}
              />
            </>
          )}
        </section>
      </Wrapper>
    )
  },
)

ProductLessons.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  currentSite: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.any,
      }),
    ),
  }),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  onCreateClass: PropTypes.func,
  onCreateSmallGroup: PropTypes.func,
  program: PropTypes.string,
  showSmallGroupCreation: PropTypes.bool,
  title: PropTypes.string.isRequired,
}
export default ProductLessons
